// TES-516 only normalizes legacy defect image metadata in this existing task aggregator; extraction belongs in a dedicated fallow task.
// fallow-ignore-file complexity
// TES-516 touches pre-existing duplicated task mapping paths without expanding their responsibilities.
// fallow-ignore-file code-duplication
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { useAuth } from "@/hooks/useAuth";
import { DefectCardData } from "@/components/defects/DefectCard";
import { InspectionCardData } from "@/components/inspection/InspectionCard";
import { DefectStatus, DefectSeverity } from "@/types";
import { createIsUserUnavailable } from "@/hooks/useUserAvailability";
import {
  isUserQueryEnabled,
  normalizeTeamScopedQueryOptions,
  type TeamScopedQueryOptions,
} from "@/hooks/teamQueryOptions";
import { normalizeDefectImagePaths } from "@/lib/defectImages";

export type TaskType =
  | "defect"
  | "inspection"
  | "verification"
  | "unassigned_defect"
  | "unassigned_inspection"
  | "unavailable_defect"
  | "unavailable_inspection"
  // TES-1863 (v2.3 §5/§6): ownerlose Mängel der eigenen Fachbereich-Gruppen (assigned_group_id ∈ meine
  // Gruppen, assigned_to NULL). Sichtbarkeitsgrenze ist die defects-RLS (group-Scope), NICHT team_id.
  | "group_unassigned_defect"
  // TES-1870 (L6-4): ownerlose Prüfungen der eigenen Fachbereich-Gruppen (assigned_group_id ∈ meine
  // Gruppen, assigned_to NULL). Sichtbarkeit via inspections-RLS (reiner group-Scope, Mig 352).
  | "group_unassigned_inspection"
  // TES-1869 (L6-3): Task-Vereinigung — Prüfpunkt-Antwort + PT-TSK-Maßnahme dem User zugewiesen.
  | "inspection_point"
  | "pt_action"
  // TES-2053 (LH §L6.1d/§L6.3): zu-verifizierende Prüfungen des Callers (aufgelöster Verifizierer
  // oder Deputy, Modell B). Quelle: SECURITY-DEFINER-RPC my_inspections_to_verify (Mig 381).
  | "inspection_verification"
  // TES-2075 (D-F3, LH §Ebene User/§Kollisionsordnung Z.272): "Als Stellvertreter" — Union über die
  // drei Deputy-Ebenen (mpp > user > group), dedupliziert. Quelle: SECURITY-DEFINER-RPC my_deputy_tasks
  // (Mig 387). Die sichtbare Sektion rendert D-UI (TES-2076); hier nur Datenzweig + Typ + Transform.
  | "deputy"
  // TES-2058 ([R12] "Meine Aufgaben" rollen-scoped): qm_admin (Org-Hülle, role_id-only) ist team-/
  // gruppenlos und träfe daher KEINE der obigen Scopes — sieht laut LH aber "alles" (Oversight, Modell B).
  // Eigener, read-only Typ (KEINE Schreib-Handler verdrahtet) statt Wiederverwendung von "defect"/
  // "inspection": die generischen Karten-Actions (z. B. AssignAction) prüfen nur Status/Zuweisung, NICHT
  // Rolle/Recht — ein qm_admin-Klick liefe RLS-seitig ins Leere (0 Zeilen, false-positive Toast). Quelle:
  // additive Oversight-SELECT-Policies (Mig 411), gespiegelt aus role_permissions view:global (Mig 366).
  | "oversight_defect"
  | "oversight_inspection";

/** TES-1869 (L6-3): zugewiesener Prüfpunkt (inspection_responses.assigned_to = userId). */
export interface InspectionPointData {
  id: string;
  inspection_id: string;
  checklist_item_title: string | null;
  inspection_status: string | null;
  assigned_to: string | null;
  assigned_to_profile?: { user_id: string; display_name: string | null } | null;
}

/** TES-1869 (L6-3): zugewiesene PT-TSK-Maßnahme (defect_action.assigned_to = userId) + externe PT-Referenz. */
export interface PtActionData {
  id: string;
  defect_id: string;
  title: string;
  description: string | null;
  type: string | null;
  /** Status der verlinkten PT-TSK-Task-Gruppe am Mangel (defect_task_links.pt_task_status), falls vorhanden. */
  pt_task_status: string | null;
}

// TES-2053: types.ts ist gegenüber Mig 381 stale — schmaler RPC-Client-Typ (Repo-Muster, vgl.
// useDefectReopen). Nur die ids werden gelesen; die Anzeige-Daten (Checklisten-Name, Position,
// Profile) lädt der INSPECTION_SELECT-Nachlade-Query per id nach.
type MyInspectionsToVerifyRpc = {
  rpc: (fn: "my_inspections_to_verify") => PromiseLike<{
    data: Array<{ id: string }> | null;
    error: { message: string } | null;
  }>;
};

/** TES-2075 (D-F3): eine Zeile der Vertretungs-Union (my_deputy_tasks, Mig 387) — eine offene Aufgabe,
 * die der Caller als Stellvertreter auf einer der drei Ebenen (mpp > user > group) sieht. */
export interface DeputyTaskData {
  /** Objektklasse des Ursprungs-Items ('defect' | 'inspection'). */
  entity_type: string;
  /** id des Ursprungs-Items (Mangel bzw. Prüfung). */
  entity_id: string;
  /** Titel/Kontext (Mangel-Titel bzw. Checklisten-Name der Prüfung). */
  title: string | null;
  /** Vertretungs-Ebene, auf der das Item nach der Kollisionsordnung erscheint. */
  deputy_level: "mpp" | "user" | "group";
  /** Ursprungs-Prinzipal (vertretener User) — bei Ebene mpp/user gesetzt, bei group null. */
  principal_user_id: string | null;
  /** Ursprungs-Gruppe — bei Ebene group gesetzt, sonst null. */
  principal_group_id: string | null;
  /** Status des Ursprungs-Items. */
  status: string | null;
  /** TES-2083 (Typ 11): der Deputy darf dieses Item per Wirkungsspektrum-Claim (defect_self_assign)
   * übernehmen — nur OWNERLOSE Mängel in den Gruppen des Vertretenen (assigned_to → Deputy). */
  claimable?: boolean;
}

// TES-2075: types.ts ist gegenüber Mig 387 ggf. stale — schmaler RPC-Client-Typ (Repo-Muster, vgl.
// MyInspectionsToVerifyRpc). Das self-only-Gate des RPC (auth.uid()) scoped die Zeilen auf den Caller.
type MyDeputyTasksRpc = {
  rpc: (
    fn: "my_deputy_tasks",
    args: { _uid: string },
  ) => PromiseLike<{
    data: Array<{
      entity_type: string;
      entity_id: string;
      title: string | null;
      deputy_level: string;
      principal_user_id: string | null;
      principal_group_id: string | null;
      status: string | null;
      due_date: string | null;
      claimable: boolean | null;
    }> | null;
    error: { message: string } | null;
  }>;
};

/**
 * Row shape projected by DEFECT_SELECT (TES-2325). DEFECT_SELECT is intentionally a NON-literal
 * `string` so PostgREST does not statically parse it against the generated `types.ts` — which does not
 * yet reflect Mig-404's `defects.resolved_by` (types.ts is regenerated authoritatively, NEVER edited by
 * hand — codebase-map.md / R-DERIVED-01). PostgREST therefore hands back `GenericStringError` for these
 * rows; they are re-anchored to this shape via `as unknown as` (same repo pattern as useDefectsQuery's
 * `as unknown as DefectDetailsRow[]`). `status`/`severity` stay `string` here — the mappers narrow them.
 */
interface DefectSelectRow {
  id: string;
  title: string;
  description: string | null;
  status: string;
  severity: string;
  due_date: string | null;
  created_at: string;
  assigned_to: string | null;
  created_by: string | null;
  verifying_qm: string | null;
  resolved_by: string | null;
  images: unknown;
  team_id: string | null;
  inspection: DefectCardData["inspection"];
  checklist_item: DefectCardData["checklist_item"];
}

export interface UnifiedTask {
  id: string;
  type: TaskType;
  defectData?: DefectCardData;
  inspectionData?: InspectionCardData;
  inspectionPointData?: InspectionPointData;
  ptActionData?: PtActionData;
  deputyData?: DeputyTaskData;
  status: string;
  due_date: string | null;
  team_id: string | null;
}

// Query key factory for consistent cache management
export const tasksKeys = {
  all: ["tasks"] as const,
  // TES-2058: isQmAdmin geht mit in den Key — der Oversight-Zweig hängt vom Rollen-Flag ab; ohne den
  // Key-Anteil würde ein Query-Client-Cache-Hit von VOR der Rollen-Auflösung (isQmAdmin noch false)
  // liegen bleiben, bis eine andere Invalidierung feuert.
  list: (userId?: string, teamId?: string | null, isQmAdmin?: boolean) =>
    [...tasksKeys.all, "list", userId, teamId, isQmAdmin] as const,
};

type TeamFilterableQuery<TQuery> = {
  eq: (column: "team_id", value: string) => TQuery;
};

async function fetchTasks(
  userId: string | undefined,
  teamId?: string | null,
  isQmAdmin?: boolean,
): Promise<UnifiedTask[]> {
  if (!userId) return [];

  // Helper to apply team filter to a query builder
  const applyTeamFilter = <TQuery extends TeamFilterableQuery<TQuery>>(query: TQuery): TQuery => {
    if (teamId) return query.eq("team_id", teamId);
    return query;
  };

  // Shared select strings to keep DRY
  // TES-2325: `resolved_by` (real `resolved`-Actor, Mig 404, server-gestempelt) mitladen — sonst lief der
  // Client-Vier-Augen-Guard isDefectSelfExecutor (via canVerifyDefect) auf der Tasks-Seite tot (las immer
  // undefined) und der Verify-Button blieb dem Behebenden sichtbar. Server-RPC bleibt autoritativ; dies ist
  // Defense-in-Depth (spiegelt den Detail-Pfad useDefectsQuery/DEFECTS_VIEW_COLUMNS).
  // `: string` (statt Literal-Typ) ist bewusst: `defects.resolved_by` (Mig 404) ist im generierten
  // types.ts noch nicht reflektiert (types.ts wird NUR via autoritativer Regeneration gepflegt, NIE
  // manuell — codebase-map.md, R-DERIVED-01). Bliebe DEFECT_SELECT ein String-Literal, würde PostgREST
  // die Projektion statisch gegen das stale Row-Typ parsen und `data` auf `SelectQueryError` verengen.
  // Der non-literal String umgeht das Static-Parsing (gleiches Muster wie DEFECTS_VIEW_COLUMNS via join).
  const DEFECT_SELECT: string = `
    id, title, description, status, severity, due_date, created_at,
    assigned_to, created_by, verifying_qm, resolved_by, images, team_id,
    inspection:inspections(id, checklists(name, version)),
    checklist_item:checklist_items(title)
  `;
  const INSPECTION_SELECT = `
    id, status, due_date, created_at, completed_at, assigned_to,
    created_by, position_id, notes, team_id,
    checklists(name, version), inspection_positions(name)
  `;

  // Parallelize all 7 primary queries for maximum throughput.
  // TES-2325: the four DEFECT_SELECT positions are captured as raw results and re-anchored to
  // DefectSelectRow below (DEFECT_SELECT is a non-literal string → PostgREST GenericStringError).
  // The three INSPECTION_SELECT positions keep their inferred type (INSPECTION_SELECT stays a literal).
  const [
    defectsRes,
    pendingVerificationRes,
    { data: inspections, error: inspectionsError },
    unassignedDefectsRes,
    { data: unassignedInspections },
    allAssignedDefectsRes,
    { data: allAssignedInspections },
  ] = await Promise.all([
    // 1. Defects assigned to user.
    // TES-1994: Status-Filter spiegelt das Nav-Badge (useOpenCounts fetchTaskCounts) —
    // cancelled wurde als "nicht begonnen" mitgezählt (kein Mapping in defectStatusMap),
    // closed gehört nicht in die Aufgaben-Liste (fertige Mängel leben auf der Mängel-Seite).
    applyTeamFilter(
      supabase
        .from("defects")
        .select(DEFECT_SELECT)
        .is("deleted_at", null)
        .eq("assigned_to", userId)
        .in("status", ["open", "in_progress", "resolved"])
        .order("created_at", { ascending: false }),
    ),
    // 2. Defects pending verification (verifying_qm)
    applyTeamFilter(
      supabase
        .from("defects")
        .select(DEFECT_SELECT)
        .is("deleted_at", null)
        .eq("verifying_qm", userId)
        .eq("status", "resolved")
        .order("created_at", { ascending: false }),
    ),
    // 3. Inspections assigned to user.
    // TES-1994: stornierte Prüfungen raus (zählten als "nicht begonnen"); completed bleibt
    // bewusst drin — der "Erledigt"-Tab der Seite zeigt sie.
    applyTeamFilter(
      supabase
        .from("inspections")
        .select(INSPECTION_SELECT)
        .eq("assigned_to", userId)
        .neq("status", "cancelled")
        .order("created_at", { ascending: false }),
    ),
    // 4. Unassigned defects created by user
    applyTeamFilter(
      supabase
        .from("defects")
        .select(DEFECT_SELECT)
        .is("deleted_at", null)
        .eq("created_by", userId)
        .is("assigned_to", null)
        .in("status", ["open", "in_progress"])
        .order("created_at", { ascending: false }),
    ),
    // 5. Unassigned inspections created by user
    applyTeamFilter(
      supabase
        .from("inspections")
        .select(INSPECTION_SELECT)
        .eq("created_by", userId)
        .is("assigned_to", null)
        .in("status", ["pending", "in_progress"])
        .order("created_at", { ascending: false }),
    ),
    // 6. ALL assigned defects (unavailable assignee check)
    // TES-2302: qm_admin verwirft den Output dieser Query (isQmAdmin ? [] beim
    // unavailableDefectTasks-Transform) — die team-/gruppenlose, tenant-weite Full-Select gar nicht
    // erst feuern (sie verdoppelte sonst die Tenant-Payload jedes Ladevorgangs ohne jeden Nutzen).
    // Regulärer User: unverändert parallel in diesem Promise.all.
    isQmAdmin
      ? Promise.resolve({ data: null, error: null })
      : applyTeamFilter(
          supabase
            .from("defects")
            .select(DEFECT_SELECT)
            .is("deleted_at", null)
            .not("assigned_to", "is", null)
            .in("status", ["open", "in_progress", "resolved"]),
        ),
    // 7. ALL assigned inspections (unavailable assignee check)
    // TES-2302: analog Query 6 — für qm_admin übersprungen (Output wird ohnehin verworfen).
    isQmAdmin
      ? Promise.resolve({ data: null, error: null })
      : applyTeamFilter(
          supabase
            .from("inspections")
            .select(INSPECTION_SELECT)
            .not("assigned_to", "is", null)
            .in("status", ["pending", "in_progress"]),
        ),
  ]);

  // TES-2325: re-anchor the DEFECT_SELECT rows (GenericStringError from the non-literal select) to the
  // real projected shape. `error` is carried through for the same console diagnostics as before.
  const defects = defectsRes.data as unknown as DefectSelectRow[] | null;
  const defectsError = defectsRes.error;
  const pendingVerification = pendingVerificationRes.data as unknown as DefectSelectRow[] | null;
  const verificationError = pendingVerificationRes.error;
  const unassignedDefects = unassignedDefectsRes.data as unknown as DefectSelectRow[] | null;
  const allAssignedDefects = allAssignedDefectsRes.data as unknown as DefectSelectRow[] | null;

  if (defectsError) console.error("Error fetching defects:", defectsError);
  if (verificationError) console.error("Error fetching pending verification:", verificationError);
  if (inspectionsError) console.error("Error fetching inspections:", inspectionsError);

  // TES-1863: ownerlose Mängel der eigenen Fachbereich-Gruppen. NICHT auf team_id zurückfallen — die
  // Sichtbarkeitsgrenze ist die defects-RLS (group-Scope, Mig 332).
  // TES-1994: "meine Gruppen" aus group_effective_members (statisch ∪ dynamisch, Resolver Mig 364) —
  // deckungsgleich zur RLS (user_group_ids); die alte group_members-Quelle ließ DYNAMISCHE Mitglieder
  // die Gruppen-Queue nicht sehen (gleiche Quelle wie der TES-1989-Fix in der Mängel-Liste).
  // TES-2060 (R13a): Owner/Deputy sind NICHT zwingend Mitglied — ihre Gruppen (group_owners_effective)
  // zusätzlich einbeziehen, damit unbesetzte Gruppen-Mängel auch für Owner/Deputy unter "Meine Aufgaben"
  // erscheinen. Deckungsgleich zur additiven SELECT-Policy "View defects as group owner" (Mig 383).
  /* istanbul ignore next -- data-fetch wiring; predicate/visibility covered by tasksHelpers unit + pgTAP/E2E */
  const [{ data: myGroups }, { data: myOwnedGroups }] = await Promise.all([
    supabase.from("group_effective_members").select("group_id").eq("user_id", userId),
    supabase.from("group_owners_effective").select("group_id").eq("user_id", userId),
  ]);
  /* istanbul ignore next -- group-id mapping (data-wiring; fetchTasks is not unit-covered) */
  const myGroupIds = Array.from(
    new Set(
      [...(myGroups || []), ...(myOwnedGroups || [])]
        .map((g) => g.group_id)
        .filter((id): id is string => id !== null),
    ),
  );
  /* istanbul ignore next -- data-fetch wiring (group-scope query); covered by pgTAP RLS + E2E */
  const groupUnassignedDefectsRes =
    myGroupIds.length > 0
      ? await supabase
          .from("defects")
          .select(DEFECT_SELECT)
          .is("deleted_at", null)
          .is("assigned_to", null)
          .in("assigned_group_id", myGroupIds)
          .in("status", ["open", "in_progress"])
          .order("created_at", { ascending: false })
      : { data: null };
  // TES-2325: re-anchor DEFECT_SELECT rows (non-literal select → GenericStringError).
  const groupUnassignedDefects = groupUnassignedDefectsRes.data as unknown as
    DefectSelectRow[] | null;

  // TES-1870 (L6-4): ownerlose Prüfungen der eigenen Fachbereich-Gruppen (assigned_group_id ∈ meine Gruppen,
  // assigned_to NULL). Reiner group-Scope (Mig 352) — die inspections-RLS garantiert die Sichtbarkeit.
  /* istanbul ignore next -- data-fetch wiring (group-scope query); covered by pgTAP RLS + E2E */
  const { data: groupUnassignedInspections } =
    myGroupIds.length > 0
      ? await supabase
          .from("inspections")
          .select(INSPECTION_SELECT)
          .is("assigned_to", null)
          .in("assigned_group_id", myGroupIds)
          .in("status", ["pending", "in_progress"])
          .order("created_at", { ascending: false })
      : { data: null };

  // TES-1869 (L6-3): Task-Vereinigung — dem User zugewiesene Prüfpunkt-Antworten + PT-TSK-Maßnahmen.
  // assigned_to-gefiltert (Meine Aufgaben); Sichtbarkeit garantiert die jeweilige RLS (inspection_responses
  // group-Scope Mig 333, defect_action group-Scope Mig 351). Keine select('*') — explizite Spalten.
  const [{ data: myInspectionPoints }, { data: myPtActions }, inspectionsToVerifyRpc] =
    await Promise.all([
      supabase
        .from("inspection_responses")
        .select("id, inspection_id, assigned_to, inspections(status), checklist_items(title)")
        .eq("assigned_to", userId),
      supabase
        .from("defect_action")
        .select(
          "id, defect_id, title, description, type, status, due_date, assigned_to, defect_task_links(pt_task_status)",
        )
        .eq("assigned_to", userId)
        .order("due_date", { ascending: true, nullsFirst: false }),
      // TES-2053 (LH §L6.3): zu-verifizierende Prüfungen des Callers (Verifizierer/Deputy) via RPC.
      (supabase as unknown as MyInspectionsToVerifyRpc).rpc("my_inspections_to_verify"),
    ]);

  if (inspectionsToVerifyRpc.error)
    console.error("Error fetching inspections to verify:", inspectionsToVerifyRpc.error);

  // TES-2053: Anzeige-Daten analog der übrigen inspection-Tasks per id nachladen — die RPC liefert
  // nackte inspections-Zeilen ohne Joins. Sichtbarkeit garantiert die additive Verifizierer-
  // SELECT-Policy (Mig 375); Team-Filter wie bei den Primär-Queries.
  const inspectionsToVerifyIds = (inspectionsToVerifyRpc.data ?? []).map((i) => i.id);
  const { data: inspectionsToVerify } =
    inspectionsToVerifyIds.length > 0
      ? await applyTeamFilter(
          supabase
            .from("inspections")
            .select(INSPECTION_SELECT)
            .in("id", inspectionsToVerifyIds)
            .order("created_at", { ascending: false }),
        )
      : { data: null };

  // TES-2075 (D-F3): "Als Stellvertreter" — Union über die drei Deputy-Ebenen (mpp > user > group,
  // kollisions-dedupliziert) via SECURITY-DEFINER-RPC my_deputy_tasks (Mig 387). Bewusst isolierter,
  // minimal-invasiver Zweig: kein Umbau der Primär-Queries, kein Profile-/Team-Enrichment (D-UI/TES-2076
  // rendert deputyData direkt). RLS-gescopt über das self-only-Gate des RPC (auth.uid() = userId).
  const deputyTasksRpc = await (supabase as unknown as MyDeputyTasksRpc).rpc("my_deputy_tasks", {
    _uid: userId,
  });
  if (deputyTasksRpc.error) console.error("Error fetching deputy tasks:", deputyTasksRpc.error);
  const deputyTasks: UnifiedTask[] = (deputyTasksRpc.data ?? []).map((r) => ({
    // Eigener id-Namespace, damit ein Item, das AUCH als reguläre Task erscheint, keinen React-Key-
    // Konflikt erzeugt (die Vertretungs-Sektion ist eine getrennte Sicht).
    id: `deputy:${r.entity_type}:${r.entity_id}`,
    type: "deputy" as TaskType,
    status: r.status ?? "",
    due_date: r.due_date,
    team_id: null,
    deputyData: {
      entity_type: r.entity_type,
      entity_id: r.entity_id,
      title: r.title,
      deputy_level: r.deputy_level as "mpp" | "user" | "group",
      principal_user_id: r.principal_user_id,
      principal_group_id: r.principal_group_id,
      status: r.status,
      // TES-2083: ownerlose Gruppen-Mängel des Vertretenen sind per Deputy-Claim übernehmbar.
      claimable: r.claimable ?? false,
    },
  }));

  // TES-2058 ([R12]): qm_admin-Oversight — TEAM-/GRUPPEN-UNABHÄNGIG alle "braucht Aktion"-Mängel/
  // Prüfungen des Tenants (Modell B, Mig 411 macht die seit Mig 366 gehaltene view:global-Permission
  // row-level wirksam). Bewusst NICHT auf assigned_to/verifying_qm gefiltert (das ist ja der Punkt:
  // qm_admin sieht ALLES, nicht nur eigene Zuweisungen). Gleicher Status-Cut wie Query 1/3 (TES-1994:
  // closed/cancelled gehören nicht in die Aufgaben-Liste). `applyTeamFilter` respektiert den optionalen
  // Team-Dropdown-Filter der Seite, ist aber sonst tenant-weit.
  const [oversightDefectsRes, { data: oversightInspections }] = isQmAdmin
    ? await Promise.all([
        applyTeamFilter(
          supabase
            .from("defects")
            .select(DEFECT_SELECT)
            .is("deleted_at", null)
            .in("status", ["open", "in_progress", "resolved"])
            .order("created_at", { ascending: false }),
        ),
        applyTeamFilter(
          supabase
            .from("inspections")
            .select(INSPECTION_SELECT)
            .in("status", ["pending", "in_progress"])
            .order("created_at", { ascending: false }),
        ),
      ])
    : [{ data: null }, { data: null }];
  // TES-2325: re-anchor DEFECT_SELECT rows (non-literal select → GenericStringError).
  const oversightDefects = oversightDefectsRes.data as unknown as DefectSelectRow[] | null;

  // FAM19: local helper — collect nullable user-ID fields from a list of records into a Set
  function collectUserIds<
    T extends Partial<Record<"assigned_to" | "created_by" | "verifying_qm", string | null>>,
  >(
    items: T[] | null | undefined,
    target: Set<string>,
    fields: (keyof T & ("assigned_to" | "created_by" | "verifying_qm"))[],
  ): void {
    items?.forEach((item) => {
      for (const field of fields) {
        const val = item[field];
        if (val) target.add(val as string);
      }
    });
  }

  // Collect all user IDs for profile lookup
  const userIds = new Set<string>();
  collectUserIds(defects, userIds, ["assigned_to", "created_by", "verifying_qm"]);
  collectUserIds(pendingVerification, userIds, ["assigned_to", "created_by", "verifying_qm"]);
  collectUserIds(inspections, userIds, ["assigned_to", "created_by"]);
  collectUserIds(unassignedDefects, userIds, ["created_by", "verifying_qm"]);
  /* istanbul ignore next -- group-queue id collection (data-wiring; fetchTasks is not unit-covered) */
  collectUserIds(groupUnassignedDefects, userIds, ["created_by", "verifying_qm"]);
  /* istanbul ignore next -- group-queue id collection (data-wiring; fetchTasks is not unit-covered) */
  collectUserIds(groupUnassignedInspections, userIds, ["created_by"]);
  collectUserIds(unassignedInspections, userIds, ["created_by"]);
  collectUserIds(allAssignedDefects, userIds, ["assigned_to", "created_by", "verifying_qm"]);
  collectUserIds(allAssignedInspections, userIds, ["assigned_to", "created_by"]);
  // TES-2053: Prüfer/Ersteller der zu-verifizierenden Prüfungen mit auflösen.
  collectUserIds(inspectionsToVerify, userIds, ["assigned_to", "created_by"]);
  // TES-2058: qm_admin-Oversight — Zuweisungs-/Ersteller-/Verifizierer-Profile mit auflösen.
  /* istanbul ignore next -- oversight id collection (data-wiring; fetchTasks is not unit-covered) */
  collectUserIds(oversightDefects, userIds, ["assigned_to", "created_by", "verifying_qm"]);
  /* istanbul ignore next -- oversight id collection (data-wiring; fetchTasks is not unit-covered) */
  collectUserIds(oversightInspections, userIds, ["assigned_to", "created_by"]);

  // Collect team IDs for team name resolution
  const teamIds = new Set<string>();
  defects?.forEach((d) => {
    if (d.team_id) teamIds.add(d.team_id);
  });
  pendingVerification?.forEach((d) => {
    if (d.team_id) teamIds.add(d.team_id);
  });
  inspections?.forEach((i) => {
    if (i.team_id) teamIds.add(i.team_id);
  });
  unassignedDefects?.forEach((d) => {
    if (d.team_id) teamIds.add(d.team_id);
  });
  /* istanbul ignore next -- group-queue team-id collection (data-wiring; fetchTasks is not unit-covered) */
  groupUnassignedDefects?.forEach((d) => {
    if (d.team_id) teamIds.add(d.team_id);
  });
  /* istanbul ignore next -- group-queue team-id collection (data-wiring; fetchTasks is not unit-covered) */
  groupUnassignedInspections?.forEach((i) => {
    if (i.team_id) teamIds.add(i.team_id);
  });
  unassignedInspections?.forEach((i) => {
    if (i.team_id) teamIds.add(i.team_id);
  });
  allAssignedDefects?.forEach((d) => {
    if (d.team_id) teamIds.add(d.team_id);
  });
  allAssignedInspections?.forEach((i) => {
    if (i.team_id) teamIds.add(i.team_id);
  });
  // TES-2053: Team-Namen auch für die zu-verifizierenden Prüfungen auflösen.
  inspectionsToVerify?.forEach((i) => {
    if (i.team_id) teamIds.add(i.team_id);
  });
  // TES-2058: Team-Namen auch für die qm_admin-Oversight-Zeilen auflösen.
  /* istanbul ignore next -- oversight team-id collection (data-wiring; fetchTasks is not unit-covered) */
  oversightDefects?.forEach((d) => {
    if (d.team_id) teamIds.add(d.team_id);
  });
  /* istanbul ignore next -- oversight team-id collection (data-wiring; fetchTasks is not unit-covered) */
  oversightInspections?.forEach((i) => {
    if (i.team_id) teamIds.add(i.team_id);
  });

  // Fetch profiles and team names in parallel
  const profilesMap: Record<
    string,
    {
      user_id: string;
      first_name: string | null;
      last_name: string | null;
      email: string;
      display_name: string | null;
      avatar_url: string | null;
      is_disabled: boolean | null;
      azure_disabled_at: string | null;
    }
  > = {};

  const teamMap: Record<string, string> = {};

  const [profilesResult, teamsResult] = await Promise.all([
    userIds.size > 0
      ? supabase
          .from("profiles")
          .select(
            "user_id, first_name, last_name, email, display_name, avatar_url, is_disabled, azure_disabled_at",
          )
          .in("user_id", Array.from(userIds))
      : Promise.resolve({ data: null }),
    teamIds.size > 0
      ? supabase.from("teams").select("id, name").in("id", Array.from(teamIds))
      : Promise.resolve({ data: null }),
  ]);

  if (profilesResult.data) {
    profilesResult.data.forEach((p) => {
      profilesMap[p.user_id] = p;
    });
  }
  if (teamsResult.data) {
    teamsResult.data.forEach((t) => {
      teamMap[t.id] = t.name;
    });
  }

  // Fetch comment counts for all defects
  /* istanbul ignore next -- group-queue id contribution (data-wiring; fetchTasks is not unit-covered) */
  const groupUnassignedDefectIds = (groupUnassignedDefects || []).map((d) => d.id);
  /* istanbul ignore next -- oversight id contribution (data-wiring; fetchTasks is not unit-covered) */
  const oversightDefectIds = (oversightDefects || []).map((d) => d.id);
  const allDefectIds = [
    ...(defects?.map((d) => d.id) || []),
    ...(pendingVerification?.map((d) => d.id) || []),
    ...(unassignedDefects?.map((d) => d.id) || []),
    ...groupUnassignedDefectIds,
    ...(allAssignedDefects?.map((d) => d.id) || []),
    ...oversightDefectIds,
  ];
  const commentCounts: Record<string, number> = {};
  if (allDefectIds.length > 0) {
    const { data: comments } = await supabase
      .from("defect_comments")
      .select("defect_id")
      .in("defect_id", allDefectIds);

    comments?.forEach((c) => {
      commentCounts[c.defect_id] = (commentCounts[c.defect_id] || 0) + 1;
    });
  }

  const isUserUnavailable = createIsUserUnavailable(profilesMap);

  // Transform defects to unified tasks
  const defectTasks: UnifiedTask[] = (defects || []).map((d) => ({
    id: d.id,
    type: "defect" as TaskType,
    status: d.status,
    due_date: d.due_date,
    team_id: d.team_id,
    defectData: {
      id: d.id,
      title: d.title,
      description: d.description,
      severity: d.severity as DefectSeverity,
      status: d.status as DefectStatus,
      created_at: d.created_at,
      due_date: d.due_date,
      images: normalizeDefectImagePaths(d.images),
      assigned_to: d.assigned_to,
      created_by: d.created_by,
      verifying_qm: d.verifying_qm,
      // TES-2325: realen resolved-Actor mitreichen, damit der Vier-Augen-Guard (canVerifyDefect) den
      // Verify-Button dem Behebenden ausblendet (Defense-in-Depth; Server-RPC bleibt autoritativ).
      resolved_by: d.resolved_by,
      assigned_to_profile: d.assigned_to ? profilesMap[d.assigned_to] : null,
      created_by_profile: d.created_by ? profilesMap[d.created_by] : null,
      verifying_qm_profile: d.verifying_qm ? profilesMap[d.verifying_qm] : null,
      inspection: d.inspection,
      checklist_item: d.checklist_item,
      comments_count: commentCounts[d.id] || 0,
      isAssigneeUnavailable: isUserUnavailable(d.assigned_to),
      team_name: d.team_id ? teamMap[d.team_id] || null : null,
    },
  }));

  // Transform pending verification defects
  const verificationTasks: UnifiedTask[] = (pendingVerification || [])
    .filter((d) => !defects?.some((existing) => existing.id === d.id))
    .map((d) => ({
      id: d.id,
      type: "verification" as TaskType,
      status: d.status,
      due_date: d.due_date,
      team_id: d.team_id,
      defectData: {
        id: d.id,
        title: d.title,
        description: d.description,
        severity: d.severity as DefectSeverity,
        status: d.status as DefectStatus,
        created_at: d.created_at,
        due_date: d.due_date,
        images: normalizeDefectImagePaths(d.images),
        assigned_to: d.assigned_to,
        created_by: d.created_by,
        verifying_qm: d.verifying_qm,
        // TES-2325: realen resolved-Actor mitreichen, damit der Vier-Augen-Guard (canVerifyDefect) den
        // Verify-Button dem Behebenden ausblendet (Defense-in-Depth; Server-RPC bleibt autoritativ).
        resolved_by: d.resolved_by,
        assigned_to_profile: d.assigned_to ? profilesMap[d.assigned_to] : null,
        created_by_profile: d.created_by ? profilesMap[d.created_by] : null,
        verifying_qm_profile: d.verifying_qm ? profilesMap[d.verifying_qm] : null,
        inspection: d.inspection,
        checklist_item: d.checklist_item,
        comments_count: commentCounts[d.id] || 0,
        isAssigneeUnavailable: isUserUnavailable(d.assigned_to),
        team_name: d.team_id ? teamMap[d.team_id] || null : null,
      },
    }));

  // Transform inspections
  const inspectionTasks: UnifiedTask[] = (inspections || []).map((i) => ({
    id: i.id,
    type: "inspection" as TaskType,
    status: i.status,
    due_date: i.due_date,
    team_id: i.team_id,
    inspectionData: {
      id: i.id,
      status: i.status as "pending" | "in_progress" | "completed" | "cancelled",
      due_date: i.due_date,
      completed_at: i.completed_at,
      assigned_to: i.assigned_to,
      created_by: i.created_by,
      checklists: i.checklists as { name: string; version?: string | null },
      inspection_positions: i.inspection_positions as { name: string } | null,
      assigned_profile: i.assigned_to ? profilesMap[i.assigned_to] : null,
      created_profile: i.created_by ? profilesMap[i.created_by] : null,
      isAssigneeUnavailable: isUserUnavailable(i.assigned_to),
      team_name: i.team_id ? teamMap[i.team_id] || null : null,
    },
  }));

  // TES-2053: transform zu-verifizierende Prüfungen. Dedupe gegen die eigenen zugewiesenen
  // Prüfungen (Muster verificationTasks): ist die abgeschlossene Prüfung dem Caller selbst
  // zugewiesen, bleibt sie eine "inspection"-Task und erscheint nicht doppelt.
  const inspectionVerificationTasks: UnifiedTask[] = (inspectionsToVerify || [])
    .filter((i) => !inspections?.some((existing) => existing.id === i.id))
    .map((i) => ({
      id: i.id,
      type: "inspection_verification" as TaskType,
      status: i.status,
      due_date: i.due_date,
      team_id: i.team_id,
      inspectionData: {
        id: i.id,
        status: i.status as "pending" | "in_progress" | "completed" | "cancelled",
        due_date: i.due_date,
        completed_at: i.completed_at,
        assigned_to: i.assigned_to,
        created_by: i.created_by,
        checklists: i.checklists as { name: string; version?: string | null },
        inspection_positions: i.inspection_positions as { name: string } | null,
        assigned_profile: i.assigned_to ? profilesMap[i.assigned_to] : null,
        created_profile: i.created_by ? profilesMap[i.created_by] : null,
        isAssigneeUnavailable: isUserUnavailable(i.assigned_to),
        team_name: i.team_id ? teamMap[i.team_id] || null : null,
      },
    }));

  // Transform unassigned defects
  const unassignedDefectTasks: UnifiedTask[] = (unassignedDefects || []).map((d) => ({
    id: d.id,
    type: "unassigned_defect" as TaskType,
    status: d.status,
    due_date: d.due_date,
    team_id: d.team_id,
    defectData: {
      id: d.id,
      title: d.title,
      description: d.description,
      severity: d.severity as DefectSeverity,
      status: d.status as DefectStatus,
      created_at: d.created_at,
      due_date: d.due_date,
      images: normalizeDefectImagePaths(d.images),
      assigned_to: d.assigned_to,
      created_by: d.created_by,
      verifying_qm: d.verifying_qm,
      // TES-2325: realen resolved-Actor mitreichen, damit der Vier-Augen-Guard (canVerifyDefect) den
      // Verify-Button dem Behebenden ausblendet (Defense-in-Depth; Server-RPC bleibt autoritativ).
      resolved_by: d.resolved_by,
      assigned_to_profile: null,
      created_by_profile: d.created_by ? profilesMap[d.created_by] : null,
      verifying_qm_profile: d.verifying_qm ? profilesMap[d.verifying_qm] : null,
      inspection: d.inspection,
      checklist_item: d.checklist_item,
      comments_count: commentCounts[d.id] || 0,
      team_name: d.team_id ? teamMap[d.team_id] || null : null,
    },
  }));

  // TES-1863: transform ownerlose Gruppen-Mängel. Dedupe gegen die eigenen unassigned-Mängel (created_by=me),
  // damit ein selbst erfasster ownerloser Mangel nicht doppelt erscheint.
  /* istanbul ignore next -- transform wiring for the ownerless-group queue (TES-1863); shape covered elsewhere */
  const ownUnassignedIds = new Set((unassignedDefects || []).map((d) => d.id));
  /* istanbul ignore next -- transform wiring; the predicate/stat logic is unit-tested in tasksHelpers */
  const groupUnassignedDefectTasks: UnifiedTask[] = (groupUnassignedDefects || [])
    .filter((d) => !ownUnassignedIds.has(d.id))
    .map((d) => ({
      id: d.id,
      type: "group_unassigned_defect" as TaskType,
      status: d.status,
      due_date: d.due_date,
      team_id: d.team_id,
      defectData: {
        id: d.id,
        title: d.title,
        description: d.description,
        severity: d.severity as DefectSeverity,
        status: d.status as DefectStatus,
        created_at: d.created_at,
        due_date: d.due_date,
        images: normalizeDefectImagePaths(d.images),
        assigned_to: d.assigned_to,
        created_by: d.created_by,
        verifying_qm: d.verifying_qm,
        // TES-2325: realen resolved-Actor mitreichen, damit der Vier-Augen-Guard (canVerifyDefect) den
        // Verify-Button dem Behebenden ausblendet (Defense-in-Depth; Server-RPC bleibt autoritativ).
        resolved_by: d.resolved_by,
        assigned_to_profile: null,
        created_by_profile: d.created_by ? profilesMap[d.created_by] : null,
        verifying_qm_profile: d.verifying_qm ? profilesMap[d.verifying_qm] : null,
        inspection: d.inspection,
        checklist_item: d.checklist_item,
        comments_count: commentCounts[d.id] || 0,
        team_name: d.team_id ? teamMap[d.team_id] || null : null,
      },
    }));

  // Transform unassigned inspections
  const unassignedInspectionTasks: UnifiedTask[] = (unassignedInspections || []).map((i) => ({
    id: i.id,
    type: "unassigned_inspection" as TaskType,
    status: i.status,
    due_date: i.due_date,
    team_id: i.team_id,
    inspectionData: {
      id: i.id,
      status: i.status as "pending" | "in_progress" | "completed" | "cancelled",
      due_date: i.due_date,
      completed_at: i.completed_at,
      assigned_to: i.assigned_to,
      created_by: i.created_by,
      checklists: i.checklists as { name: string; version?: string | null },
      inspection_positions: i.inspection_positions as { name: string } | null,
      assigned_profile: null,
      created_profile: i.created_by ? profilesMap[i.created_by] : null,
      isAssigneeUnavailable: false,
      team_name: i.team_id ? teamMap[i.team_id] || null : null,
    },
  }));

  // TES-1870 (L6-4): transform ownerlose Gruppen-Prüfungen (Mein-Team-Queue, Prüfungs-Seite).
  /* istanbul ignore next -- transform wiring for the ownerless-group inspection queue (TES-1870); shape covered elsewhere */
  const groupUnassignedInspectionTasks: UnifiedTask[] = (groupUnassignedInspections || []).map(
    (i) => ({
      id: i.id,
      type: "group_unassigned_inspection" as TaskType,
      status: i.status,
      due_date: i.due_date,
      team_id: i.team_id,
      inspectionData: {
        id: i.id,
        status: i.status as "pending" | "in_progress" | "completed" | "cancelled",
        due_date: i.due_date,
        completed_at: i.completed_at,
        assigned_to: i.assigned_to,
        created_by: i.created_by,
        checklists: i.checklists as { name: string; version?: string | null },
        inspection_positions: i.inspection_positions as { name: string } | null,
        assigned_profile: null,
        created_profile: i.created_by ? profilesMap[i.created_by] : null,
        isAssigneeUnavailable: false,
        team_name: i.team_id ? teamMap[i.team_id] || null : null,
      },
    }),
  );

  // Filter and transform unavailable assignee defects
  const existingDefectIds = new Set([
    ...(defects?.map((d) => d.id) || []),
    ...(pendingVerification?.map((d) => d.id) || []),
  ]);
  // TES-2058: qm_admin bleibt read-only (kein defects:manage mehr) — die "unavailable assignee"-Karte
  // rendert AKTIONSFÄHIGE Buttons (Assign/Edit/Cancel/…, s. renderUnassignedDefectCard in Tasks.tsx),
  // die für qm_admin RLS-seitig ins Leere liefen. Mig 411 (qm_admin-Oversight-SELECT) würde die
  // TEAM-/GRUPPEN-lose Query 6 für qm_admin sonst tenant-weit befüllen → doppelte Zeilen
  // (unavailable_defect UND oversight_defect für denselben Datensatz). Bulk-Reassign ist kein
  // Oversight-Flow — für qm_admin bewusst leer. TES-2302: Query 6 wird für qm_admin ohnehin
  // übersprungen (allAssignedDefects = null); dieser Guard bleibt als Defense-in-Depth.
  const unavailableDefectTasks: UnifiedTask[] = isQmAdmin
    ? []
    : (allAssignedDefects || [])
        .filter((d) => isUserUnavailable(d.assigned_to) && !existingDefectIds.has(d.id))
        .map((d) => ({
          id: d.id,
          type: "unavailable_defect" as TaskType,
          status: d.status,
          due_date: d.due_date,
          team_id: d.team_id,
          defectData: {
            id: d.id,
            title: d.title,
            description: d.description,
            severity: d.severity as DefectSeverity,
            status: d.status as DefectStatus,
            created_at: d.created_at,
            due_date: d.due_date,
            images: normalizeDefectImagePaths(d.images),
            assigned_to: d.assigned_to,
            created_by: d.created_by,
            verifying_qm: d.verifying_qm,
            // TES-2325: realen resolved-Actor mitreichen, damit der Vier-Augen-Guard (canVerifyDefect) den
            // Verify-Button dem Behebenden ausblendet (Defense-in-Depth; Server-RPC bleibt autoritativ).
            resolved_by: d.resolved_by,
            assigned_to_profile: d.assigned_to ? profilesMap[d.assigned_to] : null,
            created_by_profile: d.created_by ? profilesMap[d.created_by] : null,
            verifying_qm_profile: d.verifying_qm ? profilesMap[d.verifying_qm] : null,
            inspection: d.inspection,
            checklist_item: d.checklist_item,
            comments_count: commentCounts[d.id] || 0,
            isAssigneeUnavailable: true,
            team_name: d.team_id ? teamMap[d.team_id] || null : null,
          },
        }));

  // Filter and transform unavailable assignee inspections
  const existingInspectionIds = new Set(inspections?.map((i) => i.id) || []);
  // TES-2058: gleicher Guard wie unavailableDefectTasks — qm_admin bleibt read-only, kein
  // Bulk-Reassign-Einstieg über eine potentiell tenant-weit befüllte (Mig 411) Query 7.
  // TES-2302: Query 7 wird für qm_admin ohnehin übersprungen (allAssignedInspections = null).
  const unavailableInspectionTasks: UnifiedTask[] = isQmAdmin
    ? []
    : (allAssignedInspections || [])
        .filter((i) => isUserUnavailable(i.assigned_to) && !existingInspectionIds.has(i.id))
        .map((i) => ({
          id: i.id,
          type: "unavailable_inspection" as TaskType,
          status: i.status,
          due_date: i.due_date,
          team_id: i.team_id,
          inspectionData: {
            id: i.id,
            status: i.status as "pending" | "in_progress" | "completed" | "cancelled",
            due_date: i.due_date,
            completed_at: i.completed_at,
            assigned_to: i.assigned_to,
            created_by: i.created_by,
            position_id: i.position_id,
            notes: i.notes,
            checklists: i.checklists as { name: string; version?: string | null },
            inspection_positions: i.inspection_positions as { name: string } | null,
            assigned_profile: i.assigned_to ? profilesMap[i.assigned_to] : null,
            created_profile: i.created_by ? profilesMap[i.created_by] : null,
            isAssigneeUnavailable: true,
            team_name: i.team_id ? teamMap[i.team_id] || null : null,
          },
        }));

  // TES-1869 (L6-3): Prüfpunkt-Antwort-Tasks (inspection_responses dem User zugewiesen).
  const inspectionPointTasks: UnifiedTask[] = (myInspectionPoints || []).map((r) => {
    const insp = r.inspections as { status?: string | null } | null;
    const ci = r.checklist_items as { title?: string | null } | null;
    return {
      id: r.id,
      type: "inspection_point" as TaskType,
      status: insp?.status ?? "pending",
      due_date: null,
      team_id: null,
      inspectionPointData: {
        id: r.id,
        inspection_id: r.inspection_id,
        checklist_item_title: ci?.title ?? null,
        inspection_status: insp?.status ?? null,
        assigned_to: r.assigned_to,
      },
    };
  });

  // TES-1869 (L6-3): PT-TSK-Maßnahme-Tasks (defect_action dem User zugewiesen) + externe PT-Referenz.
  const ptActionTasks: UnifiedTask[] = (myPtActions || []).map((a) => {
    const links = (a.defect_task_links as { pt_task_status?: string | null }[] | null) ?? [];
    return {
      id: a.id,
      type: "pt_action" as TaskType,
      status: a.status,
      due_date: a.due_date,
      team_id: null,
      ptActionData: {
        id: a.id,
        defect_id: a.defect_id,
        title: a.title,
        description: a.description,
        type: a.type,
        pt_task_status: links[0]?.pt_task_status ?? null,
      },
    };
  });

  // TES-2058 ([R12]): qm_admin-Oversight-Tasks — read-only (KEINE Schreib-Handler, s. TaskType-Kommentar).
  // Dedupe gegen ALLE eigenen Buckets (defects/verification/unassigned/group): ist qm_admin zufällig
  // AUCH Zuweisungs-/Verifizierer-/Gruppen-Empfänger eines Mangels/einer Prüfung, bleibt die bestehende
  // (aktionsfähige) Karte führend — die Oversight-Zeile würde sonst denselben Datensatz doppelt zeigen.
  /* istanbul ignore next -- transform wiring for the oversight queue (TES-2058); predicate/shape is unit-tested in tasksHelpers */
  const ownDefectIds = new Set([
    ...(defects?.map((d) => d.id) || []),
    ...(pendingVerification?.map((d) => d.id) || []),
    ...(unassignedDefects?.map((d) => d.id) || []),
    ...groupUnassignedDefectIds,
  ]);
  /* istanbul ignore next -- transform wiring for the oversight queue (TES-2058); predicate/shape is unit-tested in tasksHelpers */
  const ownInspectionIds = new Set([
    ...(inspections?.map((i) => i.id) || []),
    ...(unassignedInspections?.map((i) => i.id) || []),
    ...(groupUnassignedInspections?.map((i) => i.id) || []),
    ...(inspectionsToVerify?.map((i) => i.id) || []),
  ]);
  /* istanbul ignore next -- transform wiring for the oversight queue (TES-2058); predicate/shape is unit-tested in tasksHelpers */
  const oversightDefectTasks: UnifiedTask[] = (oversightDefects || [])
    .filter((d) => !ownDefectIds.has(d.id))
    .map((d) => ({
      id: d.id,
      type: "oversight_defect" as TaskType,
      status: d.status,
      due_date: d.due_date,
      team_id: d.team_id,
      defectData: {
        id: d.id,
        title: d.title,
        description: d.description,
        severity: d.severity as DefectSeverity,
        status: d.status as DefectStatus,
        created_at: d.created_at,
        due_date: d.due_date,
        images: normalizeDefectImagePaths(d.images),
        assigned_to: d.assigned_to,
        created_by: d.created_by,
        verifying_qm: d.verifying_qm,
        // TES-2325: realen resolved-Actor mitreichen, damit der Vier-Augen-Guard (canVerifyDefect) den
        // Verify-Button dem Behebenden ausblendet (Defense-in-Depth; Server-RPC bleibt autoritativ).
        resolved_by: d.resolved_by,
        assigned_to_profile: d.assigned_to ? profilesMap[d.assigned_to] : null,
        created_by_profile: d.created_by ? profilesMap[d.created_by] : null,
        verifying_qm_profile: d.verifying_qm ? profilesMap[d.verifying_qm] : null,
        inspection: d.inspection,
        checklist_item: d.checklist_item,
        comments_count: commentCounts[d.id] || 0,
        isAssigneeUnavailable: isUserUnavailable(d.assigned_to),
        team_name: d.team_id ? teamMap[d.team_id] || null : null,
      },
    }));
  /* istanbul ignore next -- transform wiring for the oversight queue (TES-2058); predicate/shape is unit-tested in tasksHelpers */
  const oversightInspectionTasks: UnifiedTask[] = (oversightInspections || [])
    .filter((i) => !ownInspectionIds.has(i.id))
    .map((i) => ({
      id: i.id,
      type: "oversight_inspection" as TaskType,
      status: i.status,
      due_date: i.due_date,
      team_id: i.team_id,
      inspectionData: {
        id: i.id,
        status: i.status as "pending" | "in_progress" | "completed" | "cancelled",
        due_date: i.due_date,
        completed_at: i.completed_at,
        assigned_to: i.assigned_to,
        created_by: i.created_by,
        checklists: i.checklists as { name: string; version?: string | null },
        inspection_positions: i.inspection_positions as { name: string } | null,
        assigned_profile: i.assigned_to ? profilesMap[i.assigned_to] : null,
        created_profile: i.created_by ? profilesMap[i.created_by] : null,
        isAssigneeUnavailable: isUserUnavailable(i.assigned_to),
        team_name: i.team_id ? teamMap[i.team_id] || null : null,
      },
    }));

  // Combine and sort all tasks
  const allTasks = [
    ...defectTasks,
    ...verificationTasks,
    ...inspectionVerificationTasks,
    ...inspectionTasks,
    ...unassignedDefectTasks,
    ...groupUnassignedDefectTasks,
    ...groupUnassignedInspectionTasks,
    ...unassignedInspectionTasks,
    ...unavailableDefectTasks,
    ...unavailableInspectionTasks,
    ...inspectionPointTasks,
    ...ptActionTasks,
    ...deputyTasks,
    ...oversightDefectTasks,
    ...oversightInspectionTasks,
  ].sort((a, b) => {
    const dateA = a.defectData?.created_at || null;
    const dateB = b.defectData?.created_at || null;
    if (!dateA || !dateB) return 0;
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });

  return allTasks;
}

export function useTasksQuery(options?: TeamScopedQueryOptions) {
  const { user, isQmAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { teamId, enabled } = normalizeTeamScopedQueryOptions(options);

  const query = useQuery({
    queryKey: tasksKeys.list(user?.id, teamId, isQmAdmin),
    queryFn: () => fetchTasks(user?.id, teamId, isQmAdmin),
    enabled: isUserQueryEnabled(user?.id, enabled),
    placeholderData: (previousData) => previousData,
    staleTime: 30_000, // 30s — prevents 10+ queries on every tab refocus
  });

  // Real-time subscription for auto-invalidation (centralized push)
  // Realtime: defects/inspections/defect_comments decken die meisten Task-Änderungen ab; Prüfpunkt-/
  // Maßnahme-Tasks (L6-3) refreshen über staleTime(30s)+refetch (RealtimeTable-Union umfasst sie nicht).
  // TES-1994: group_members + group_members_effective zusätzlich — ändert sich die Gruppen-Zuordnung
  // des Users (statisch ODER dynamisch via Resolver), muss die Gruppen-Queue sofort nachziehen
  // (vorher blieb der Zähler stehen, bis irgendein defects-Event feuerte). Publikation: Mig 370.
  useRealtimeSubscription(
    ["defects", "inspections", "defect_comments", "group_members", "group_members_effective"],
    () => {
      queryClient.invalidateQueries({ queryKey: tasksKeys.all });
    },
    { enabled: !!user, debounceMs: 1000 },
  );

  return {
    tasks: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}
